import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowBackIcon from '@mui/icons-material/esm/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/esm/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/esm/ExpandMore';
import FactCheckIcon from '@mui/icons-material/esm/FactCheck';
import ReportProblemIcon from '@mui/icons-material/esm/ReportProblem';
import PhotoIcon from '@mui/icons-material/esm/PhotoCamera';
import RuleIcon from '@mui/icons-material/esm/Rule';
import SaveIcon from '@mui/icons-material/esm/Save';
import WarningIcon from '@mui/icons-material/esm/WarningAmber';
import tasksService, { Task } from '../../services/api/tasksService';
import inspectionsService, { InspectionRow } from '../../services/api/inspectionsService';
import { enqueueOfflineFieldEvidence } from '../../services/offline/fieldOfflineForms';
import { photoEvidencePayload } from '../../services/offline/fieldPhotoCapture';
import fieldSyncService from '../../services/offline/fieldSyncService';
import { recordInspectionFailDefect, submitInspectionVerdict } from './supervisorInspectionActions';
import { failDefectPayload, verdictLifecycleStatus } from './supervisorFieldUtils';
import TaskPhotoEvidencePanel from './TaskPhotoEvidencePanel';
import SitePlanMap from '../../components/siteplan/SitePlanMap';
import { supervisorSpecificationItems } from '../../utils/taskRoleSpecifications';

type Verdict = 'passed' | 'conditional' | 'failed';

const verdictConfig: Record<Verdict, { label: string; color: 'success' | 'warning' | 'error' }> = {
  passed: { label: 'Passed', color: 'success' },
  conditional: { label: 'Conditional - remedial work required', color: 'warning' },
  failed: { label: 'Failed', color: 'error' },
};

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const statusKey = (task?: Task | null) => String(task?.lifecycleStatus || task?.status || '').toUpperCase();

const normalizedText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length ? text : null;
};

const previousRemedialReasons = (task: Task | null, inspections: InspectionRow[]): string[] => {
  const reasons: string[] = [];
  const seen = new Set<string>();
  const addReason = (value: unknown) => {
    const text = normalizedText(value);
    if (!text) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    reasons.push(text);
  };

  addReason(task?.rejectionReason);
  task?.defects?.forEach((defect) => {
    addReason(defect.description);
    if (!normalizedText(defect.description)) addReason(defect.title);
  });
  inspections.forEach((inspection) => addReason(inspection.resultSummary));

  return reasons;
};

const taskLocation = (task: Task): string =>
  task.workLocationRef?.name ||
  task.workLocation ||
  task.component?.unit?.code ||
  task.component?.name ||
  'Site';

const assignedCrewNameFor = (task: Task): string => {
  const activeAssignment = (task.teamAssignments ?? []).find((assignment) => {
    const status = String(assignment.lifecycleStatus ?? assignment.lifecycle_status ?? '').toLowerCase();
    return !['cancelled', 'removed'].includes(status);
  });
  return activeAssignment?.team?.name
    ?? (activeAssignment as any)?.teamName
    ?? (activeAssignment as any)?.team_name
    ?? task.team?.name
    ?? 'Crew not recorded';
};

const photoNameForTask = (task?: Task | null): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
  const raw = `${task?.taskCode ?? task?.id ?? 'supervisor-task'}-${timestamp}`;
  return `${raw.replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-')}.jpg`;
};

function InfoAccordion({
  title,
  subtitle,
  icon,
  children,
  defaultExpanded = false,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ borderRadius: 1, overflow: 'hidden' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: 1, display: 'grid', placeItems: 'center', bgcolor: '#eaf1fd', color: '#1f6fe5' }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export default function SupervisorInspectionDetailPage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [inspections, setInspections] = useState<InspectionRow[]>([]);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingReasons, setSavingReasons] = useState(false);
  const [reasonsApplied, setReasonsApplied] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastPhotoName, setLastPhotoName] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoQueuedCount, setPhotoQueuedCount] = useState(0);

  const linkedInspection = useMemo(
    () => inspections.find((row) => !['passed', 'failed', 'cancelled'].includes(row.status)) ?? inspections[0] ?? null,
    [inspections]
  );
  const resultSummary = useMemo(
    () => notes.trim() || null,
    [notes]
  );
  const priorReasons = useMemo(
    () => previousRemedialReasons(task, inspections),
    [task, inspections]
  );

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const taskRow = await tasksService.getTaskById(taskId);
        const inspectionRows = taskRow.projectId
          ? await inspectionsService.list({ projectId: taskRow.projectId, taskId })
          : [];
        if (cancelled) return;
        setTask(taskRow);
        setInspections(inspectionRows);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Failed to load inspection detail.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  const queueInspectionPhoto = async (file?: File | null) => {
    if (!file || !task) return;
    setPhotoBusy(true);
    setError(null);
    try {
      const photoName = photoNameForTask(task);
      const payload = await photoEvidencePayload(file, photoName);
      await enqueueOfflineFieldEvidence(task, { ...payload, notes: 'Supervisor inspection photo evidence' });
      const synced = navigator.onLine ? await fieldSyncService.flushPending().catch(() => null) : null;
      setLastPhotoName(photoName);
      setPhotoQueuedCount((current) => current + 1);
      setMessage(synced?.synced ? 'Photo evidence uploaded.' : 'Photo evidence queued for sync.');
    } catch {
      setError('Photo could not be queued.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const updateNotes = (value: string) => {
    setNotes(value);
    setReasonsApplied(false);
  };

  const applyReasons = async () => {
    if (!task) return;
    if (!resultSummary) {
      setError('Select a reason or enter defect analysis before applying.');
      return;
    }

    setSavingReasons(true);
    setError(null);
    setMessage(null);
    try {
      if (linkedInspection) {
        const updated = await inspectionsService.update(linkedInspection.id, { resultSummary });
        setInspections((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      } else {
        const updated = await tasksService.updateTask(task.id, { notes: resultSummary });
        setTask(updated);
      }
      setReasonsApplied(true);
      setMessage('Defect / rejection reasons applied.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Reasons could not be applied.');
    } finally {
      setSavingReasons(false);
    }
  };

  const submitVerdict = async (nextVerdict: Verdict) => {
    if (!task) return;
    setVerdict(nextVerdict);
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const result = await submitInspectionVerdict(task, linkedInspection, nextVerdict, { resultSummary });

      let defectNote = '';
      const isRemedialVerdict = nextVerdict === 'failed' || nextVerdict === 'conditional';
      if (isRemedialVerdict) {
        const defectPayload = failDefectPayload(task, [], resultSummary ?? notes);
        const defectResult = await recordInspectionFailDefect(
          task,
          nextVerdict === 'conditional'
            ? { ...defectPayload, title: `Conditional remedial work: ${task.title}`.slice(0, 200) }
            : defectPayload
        );
        defectNote = defectResult.queuedOffline
          ? ' Defect record queued for sync.'
          : ' Defect record created for the remedial team.';
      }

      if (result.queuedOffline) {
        const lifecycleStatus = verdictLifecycleStatus(nextVerdict);
        setTask(result.task ?? { ...task, lifecycleStatus, status: lifecycleStatus });
        setMessage(`Inspection verdict queued for sync.${defectNote}`);
      } else {
        const refreshed = await tasksService.getTaskById(task.id);
        setTask(refreshed);
        setMessage(
          nextVerdict === 'conditional'
            ? `Inspection marked conditional and returned to worker for remedial work.${defectNote}`
            : `Inspection marked ${verdictConfig[nextVerdict].label.toLowerCase()}.${defectNote}`
        );
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Verdict could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !task) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 1 }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (!task) {
    return <Alert severity="error">{error || 'Inspection task was not found.'}</Alert>;
  }

  const specs = supervisorSpecificationItems(task);
  const resources = Array.isArray(task.resourcesRequired) ? task.resourcesRequired : [];
  const assignedCrewName = assignedCrewNameFor(task);
  const acceptance = [
    'Evidence is present and readable',
    'Work location matches the dispatched task',
    'Specifications and tolerances are satisfied',
    'No unresolved safety or access issue blocks approval',
  ];

  return (
    <Stack spacing={2.5}>
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/field')} sx={{ mb: 1 }}>
          Inspections
        </Button>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontSize: 26, fontWeight: 900, letterSpacing: 0 }}>
              {task.title}
            </Typography>
            <Typography color="text.secondary">
              {[task.taskType, task.team?.name, taskLocation(task)].filter(Boolean).join(' - ')}
            </Typography>
          </Box>
          <Chip label={statusKey(task).replace(/_/g, ' ')} />
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}
      <Alert severity="info">
        Use Take photo on a phone or iPad to open the device camera. Photos are saved to this task's field evidence and sync when online.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ md: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Inspection Checklist</Typography>
            <Typography variant="body2" color="text.secondary">Task number, location, details, specifications, picking list and date.</Typography>
          </Box>
          <Button
            variant="contained"
            component="label"
            color={photoQueuedCount ? 'success' : 'primary'}
            disabled={photoBusy}
            startIcon={<PhotoIcon />}
          >
            {photoQueuedCount ? `Task photos (${photoQueuedCount})` : 'Take actual photo'}
            <input hidden type="file" accept="image/*" capture="environment" onChange={(event) => {
              const file = event.target.files?.[0] ?? null;
              void queueInspectionPhoto(file);
              event.target.value = '';
            }} />
          </Button>
        </Stack>
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}><Typography variant="caption">Task number</Typography><Typography fontWeight={900}>{task.taskCode || task.id}</Typography></Grid>
          <Grid item xs={12} md={3}><Typography variant="caption">Location</Typography><Typography fontWeight={900}>{taskLocation(task)}</Typography></Grid>
          <Grid item xs={12} md={3}><Typography variant="caption">Date</Typography><Typography fontWeight={900}>{formatDate(task.dueDate)}</Typography></Grid>
          <Grid item xs={12} md={3}><Typography variant="caption">Photo name</Typography><Typography fontWeight={900}>{lastPhotoName || photoNameForTask(task)}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="caption">Assigned to</Typography><Typography fontWeight={900}>{assignedCrewName}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="caption">Completed by</Typography><Typography fontWeight={900}>{task.completedByName ?? task.completedBy?.name ?? task.completedBy?.email ?? 'Worker not recorded'}</Typography></Grid>
          <Grid item xs={12} md={6}><Typography variant="caption">Completed at</Typography><Typography fontWeight={900}>{formatDateTime(task.completedAt ?? task.completionDate)}</Typography></Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.5}>
            <InfoAccordion title="Location" subtitle="Where to inspect" icon={<FactCheckIcon />} defaultExpanded>
              <Stack spacing={1}>
                <Typography><strong>Project:</strong> {task.project?.name ?? task.projectId ?? 'Current project'}</Typography>
                <Typography><strong>Area:</strong> {taskLocation(task)}</Typography>
                <Typography><strong>Due:</strong> {formatDate(task.dueDate)}</Typography>
                <SitePlanMap task={task} compact />
              </Stack>
            </InfoAccordion>

            <InfoAccordion title="Details / Specifications" subtitle="Work requirements" icon={<RuleIcon />} defaultExpanded>
              {specs.length === 0 ? (
                <Alert severity="info">No specifications are attached to this task.</Alert>
              ) : (
                <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                  {specs.map((spec, index) => (
                    <Typography component="li" key={`${spec.text}-${index}`}>
                      {spec.text}
                    </Typography>
                  ))}
                </Stack>
              )}
            </InfoAccordion>

            <InfoAccordion title="Quality control checks" subtitle="Review before verdict" icon={<CheckCircleIcon />}>
              <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                <Typography component="li">Confirm task evidence and completion photos.</Typography>
                <Typography component="li">Check materials/tools against the picking list.</Typography>
                <Typography component="li">Review linked QC runs and open defects.</Typography>
                <Typography component="li">Confirm the team assignment and inspected area are correct.</Typography>
              </Stack>
            </InfoAccordion>

            <InfoAccordion title="Acceptance criteria" subtitle="Pass conditions" icon={<CheckCircleIcon />}>
              <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                {acceptance.map((item) => (
                  <Typography component="li" key={item}>{item}</Typography>
                ))}
              </Stack>
            </InfoAccordion>

            <InfoAccordion title="Required evidence" subtitle="Records expected" icon={<FactCheckIcon />}>
              <Stack component="ul" spacing={1} sx={{ pl: 2, m: 0 }}>
                <Typography component="li">Photo evidence from Field Mode.</Typography>
                <Typography component="li">Completed task status and timestamp.</Typography>
                {resources.slice(0, 4).map((resource, index) => (
                  <Typography component="li" key={index}>
                    Resource check: {resource.name ?? resource.resourceName ?? resource.description ?? 'resource'}
                  </Typography>
                ))}
              </Stack>
            </InfoAccordion>

            <TaskPhotoEvidencePanel
              projectId={task.projectId}
              taskIds={[task.id]}
              title="Photos for this inspection"
            />
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.5 }}>Defect / rejection reasons</Typography>
              {priorReasons.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  <Stack spacing={0.75}>
                    <Typography variant="caption" sx={{ fontWeight: 900, textTransform: 'uppercase' }}>
                      Previous remedial reason
                    </Typography>
                    {priorReasons.slice(0, 3).map((reason) => (
                      <Typography key={reason} variant="body2">
                        {reason}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}
              <TextField
                label="Defect analysis / current inspection notes"
                value={notes}
                onChange={(event) => updateNotes(event.target.value)}
                multiline
                minRows={6}
                fullWidth
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={savingReasons ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  disabled={saving || savingReasons || !resultSummary}
                  onClick={applyReasons}
                >
                  Apply reasons
                </Button>
                {reasonsApplied && <Chip size="small" color="success" label="Notes saved - still choose a verdict" />}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, textTransform: 'uppercase' }}>
                Inspection verdict
              </Typography>
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {(Object.keys(verdictConfig) as Verdict[]).map((key) => (
                  <Button
                    key={key}
                    variant={verdict === key ? 'contained' : 'outlined'}
                    color={verdictConfig[key].color}
                    startIcon={key === 'failed' ? <ReportProblemIcon /> : key === 'conditional' ? <WarningIcon /> : <CheckCircleIcon />}
                    disabled={saving}
                    onClick={() => submitVerdict(key)}
                    fullWidth
                  >
                    {key === 'failed' ? 'Fail inspection and return to worker' : verdictConfig[key].label}
                  </Button>
                ))}
              </Stack>
              {saving && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Saving verdict</Typography>
                </Stack>
              )}
            </Paper>

            {linkedInspection && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Linked inspection</Typography>
                <Typography variant="body2" color="text.secondary">{linkedInspection.inspectionNumber}</Typography>
                <Typography variant="body2">{linkedInspection.title}</Typography>
                <Chip size="small" label={linkedInspection.status.replace(/_/g, ' ')} sx={{ mt: 1 }} />
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
